/**
 * ChartDetails Component
 * Main component displaying all astrological chart information in sections
 * Includes chart info (with Short Links), planets, and panchanga
 */

import React, { useState, useEffect } from 'react';
import { AstrologyDataList } from './Planets';
import { ChartInfoSection } from './DataSections/ChartInfoSection';
import { PanchangaSection } from './DataSections/PanchangaSection';
import { DashasSection } from './DataSections/sections/DashasSection';
import { ChartSection, useSectionPerformance } from './ChartSection';
import { JatakaResponse } from '../../../models/api';
import { SharedChartData, SharedViewMode } from '../../../types/share.types';
import './ChartDetails.css';

export interface ChartDetailsProps {
  className?: string;
  chartId?: string; // Chart ID for state persistence
  chartData?: any; // Chart-specific data for this tab (required - comes from UnifiedChartTab)
  shareMode?: boolean;           // If true, chart is loaded from share link (all sections always visible)
  sharedViewMode?: SharedViewMode;  // View mode for shared charts (author/guest)
  sharedChartData?: SharedChartData;  // Shared chart data with share_config
}

/**
 * ChartDetails Component
 * Main container for all chart information sections
 * Note: All sections are always visible, regardless of shareMode
 */
const ChartDetails: React.FC<ChartDetailsProps> = ({
  className = '',
  chartId,
  chartData,
  shareMode = false,
  sharedViewMode = 'author',
  sharedChartData
}) => {
  // Performance monitoring for sections
  const { recordRenderTime, getSectionStats } = useSectionPerformance();

  // Chart is always considered active - visibility is controlled by CSS in parent components
  const isActive = true;

  // Get API response from chartData prop (provided by UnifiedChartTab)
  const sourceApiResponse = chartData?.apiResponse;

  const [processedApiResponse, setProcessedApiResponse] = useState<JatakaResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Process API response data from props
  useEffect(() => {
    if (sourceApiResponse) {
      // Use JatakaApiResponse directly - no conversion needed
      setProcessedApiResponse(sourceApiResponse);
      setIsLoading(false);
      setError(null);
    } else {
      const isChartLoading = chartData?.isLoading ?? false;
      if (!isChartLoading) {
        setIsLoading(false);
        setError('No chart data available');
      } else {
        setIsLoading(true);
      }
    }
  }, [sourceApiResponse, chartData]);


  if (isLoading) {
    return (
      <div className={`chart-details-container ${className}`}>
        <div className="chart-details-loading">
          <div className="loading-spinner"></div>
          <span className="loading-text">Loading chart details...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`chart-details-container ${className}`}>
        <div className="chart-details-error">
          <span className="error-text">{error}</span>
        </div>
      </div>
    );
  }

  // Use chartId prop directly (no context dependency to prevent re-renders)
  const currentChartId = chartId;

  return (
    <div className={`chart-details-container ${className}`}>
      <div className="chart-details-content">
        {/* Section 0: Chart Info */}
        <div className="details-section">
          <ChartSection
            component={ChartInfoSection}
            sectionId="chart-info"
            chartId={currentChartId}
            componentProps={{
              apiResponse: processedApiResponse,
              shareMode: shareMode,
              sharedViewMode: sharedViewMode,
              sharedChartData: sharedChartData,
              chartId: currentChartId
            }}
            initiallyVisible={isActive}
            onRenderTime={recordRenderTime}
            className="chart-section-chart-info"
          />
        </div>

        {/* Section 1: Planetary Positions */}
        <div className="details-section">
          <ChartSection
            component={AstrologyDataList}
            sectionId="planets"
            chartId={currentChartId}
            componentProps={{
              showLagna: true,
              showPlanets: true,
              title: "Planetary Positions",
              apiResponse: processedApiResponse,
              chartId: currentChartId,
              shareMode: shareMode
            }}
            initiallyVisible={isActive}
            onRenderTime={recordRenderTime}
            className="chart-section-planets"
          />
        </div>

        {/* Section 2: Panchanga */}
        <div className="details-section">
          <ChartSection
            component={PanchangaSection}
            sectionId="panchanga"
            chartId={currentChartId}
            componentProps={{
              apiResponse: processedApiResponse,
              shareMode: shareMode,
              sharedViewMode: sharedViewMode,
              sharedChartData: sharedChartData
            }}
            initiallyVisible={isActive}
            onRenderTime={recordRenderTime}
            className="chart-section-panchanga"
          />
        </div>

        {/* Section 3: Dashas (Planetary Periods) */}
        <div className="details-section">
          <ChartSection
            component={DashasSection}
            sectionId="dashas"
            chartId={currentChartId}
            componentProps={{
              apiResponse: processedApiResponse
            }}
            initiallyVisible={isActive}
            onRenderTime={recordRenderTime}
            className="chart-section-dashas"
          />
        </div>
      </div>

      {/* Performance monitoring (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="chart-details-debug">
          📊 {getSectionStats().totalSections} sections, avg: {getSectionStats().averageRenderTime.toFixed(1)}ms
        </div>
      )}
    </div>
  );
};

export default ChartDetails;